import { createFileRoute } from '@tanstack/react-router'

import { LegalPage } from '@/features/legal/components/legal-page'

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
})

function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="August 1, 2026">
      <section>
        <h2>1. Introduction</h2>
        <p>
          Melodex ("we," "us," or "our") operates the AI video creation platform
          at <a href="https://melodex.app">melodex.app</a>. This Privacy Policy
          explains what personal information we collect when you use the
          service, how we use it, who we share it with, and the rights you have
          over it.
        </p>
        <p>
          By using Melodex, you agree to the practices described below. If you
          do not agree, please do not use the service.
        </p>
      </section>

      <section>
        <h2>2. Information We Collect</h2>

        <h3>2.1 Account Information</h3>
        <p>When you create an account we collect:</p>
        <ul>
          <li>Email address</li>
          <li>Display name</li>
          <li>
            A hashed version of your password (we never store your password in
            plain text)
          </li>
        </ul>

        <h3>2.2 Google Sign-In Data</h3>
        <p>
          If you choose "Continue with Google," Google shares a limited profile
          with us: your email address, your name, your Google account ID, and
          whether Google has verified that email. We request only the{' '}
          <code>openid email profile</code> scopes. We do not access your Gmail,
          Drive, Calendar, YouTube, or any other Google services.
        </p>

        <h3>2.3 Content You Create</h3>
        <p>Melodex is a creation tool. To provide the service we process:</p>
        <ul>
          <li>Audio you upload (vocals, voice samples, reference tracks)</li>
          <li>Lyrics and text prompts you write</li>
          <li>
            Visual direction you provide (scene descriptions, style preferences)
          </li>
          <li>Generated outputs (songs, images, rendered videos)</li>
          <li>Project metadata (titles, edit history, progress state)</li>
        </ul>

        <h3>2.4 Usage and Technical Data</h3>
        <p>To operate the service reliably we automatically collect:</p>
        <ul>
          <li>IP address (used for rate limiting and fraud prevention)</li>
          <li>Browser type, device type, and operating system</li>
          <li>Request logs (URL, status, duration, request ID)</li>
          <li>Error reports</li>
        </ul>

        <h3>2.5 Payment Information</h3>
        <p>
          When you purchase Generation Points (GP) or a subscription, payments
          are processed by a third-party provider. We receive confirmation of
          the transaction and a transaction ID, but we do not store your full
          card number or bank account details.
        </p>
      </section>

      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide the service and process your creations</li>
          <li>Manage your account, GP balance, and billing</li>
          <li>
            Send service-related emails (password resets, receipts, account
            notices)
          </li>
          <li>Protect against fraud, abuse, and security threats</li>
          <li>
            Improve the platform (diagnostics, performance monitoring, bug
            fixes)
          </li>
          <li>Comply with legal obligations</li>
        </ul>
        <p>
          We do not use your content or account data to train our AI models, and
          we do not sell your personal information to advertisers or data
          brokers.
        </p>
      </section>

      <section>
        <h2>4. Third-Party Service Providers</h2>
        <p>
          We use service providers to deliver Melodex. Their privacy terms and
          data-processing commitments also apply when they process a request:
        </p>
        <ul>
          <li>
            Anthropic or OpenRouter: text understanding, lyrics, scripts, and
            prompt generation
          </li>
          <li>fal.ai: scene-image and narration model hosting</li>
          <li>RunPod: song generation model hosting</li>
          <li>Cloudflare R2: storage of your uploaded and generated media</li>
          <li>Whop: subscriptions and one-time GP payments</li>
          <li>Resend: account and service email delivery</li>
          <li>
            Apatero Analytics: self-hosted, aggregate product analytics without
            advertising profiles
          </li>
          <li>
            OVH: server hosting (data centers located in the European Union)
          </li>
          <li>Google: authentication, if you choose Google Sign-In</li>
        </ul>
        <p>
          We send each provider only the information needed for the requested
          feature and do not sell your content to them.
        </p>
      </section>

      <section>
        <h2>5. Data Storage and Location</h2>
        <p>
          Your account data and content are stored in PostgreSQL and Cloudflare
          R2. The primary hosting region is the European Union. Some providers
          (for example, Anthropic, OpenRouter, RunPod, fal.ai, Whop, or Resend)
          may process your requests on servers outside the European Union. Where
          required, transfers outside the EU rely on appropriate legal
          safeguards such as Standard Contractual Clauses.
        </p>
      </section>

      <section>
        <h2>6. Data Retention</h2>
        <p>
          We retain your account data and content for as long as your account is
          active. If you delete your account, we remove your personal data from
          our production systems within thirty days. If encrypted database
          backups are active, they use a thirty-day rolling retention window, so
          deleted data may remain in a protected backup until that archive
          expires.
        </p>
        <p>
          We may retain limited information for longer where the law requires it
          (for example, tax records) or where we need it to resolve disputes or
          enforce our agreements.
        </p>
      </section>

      <section>
        <h2>7. Your Rights</h2>
        <p>
          Depending on where you live, you may have the following rights
          regarding your personal information:
        </p>
        <ul>
          <li>Access: request a copy of the data we hold about you</li>
          <li>Correction: ask us to fix inaccurate data</li>
          <li>Deletion: ask us to erase your data</li>
          <li>Portability: receive your data in a machine-readable format</li>
          <li>Objection: object to certain processing activities</li>
          <li>Withdrawal of consent: where processing is based on consent</li>
        </ul>
        <p>
          To exercise any of these rights, email{' '}
          <a href="mailto:apaterogroup@gmail.com">apaterogroup@gmail.com</a>. We
          respond within thirty days. You also have the right to lodge a
          complaint with a data protection authority in your country.
        </p>
      </section>

      <section>
        <h2>8. Cookies</h2>
        <p>
          Melodex uses essential cookies required to keep you signed in. The
          most important one is an HTTP-only refresh token cookie, which cannot
          be read by JavaScript and is sent only to the Melodex API. Our
          self-hosted analytics measures aggregate usage without advertising
          profiles. We do not use advertising cookies or cross-site tracking
          cookies on the application.
        </p>
      </section>

      <section>
        <h2>9. Children's Privacy</h2>
        <p>
          Melodex is not directed to children under thirteen, or under sixteen
          in the European Economic Area. We do not knowingly collect personal
          information from children below these ages. If you believe a child has
          provided us with personal information, please contact us and we will
          delete it.
        </p>
      </section>

      <section>
        <h2>10. Security</h2>
        <p>
          We apply industry-standard safeguards to protect your data, including
          encrypted connections (TLS), password hashing with Argon2id, short-
          lived access tokens, revocable refresh tokens, progressive login rate
          limiting, and least-privilege access to production systems. No system
          is ever completely secure. We will notify you and the appropriate
          authorities if we become aware of a personal data breach that affects
          you.
        </p>
      </section>

      <section>
        <h2>11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we
          will update the "Last updated" date at the top of this page. For
          material changes we will send you an email or show a notice inside the
          app before the changes take effect.
        </p>
      </section>

      <section>
        <h2>12. Contact</h2>
        <p>
          If you have questions about this Privacy Policy or how we handle your
          data, email{' '}
          <a href="mailto:apaterogroup@gmail.com">apaterogroup@gmail.com</a>.
        </p>
      </section>
    </LegalPage>
  )
}
